import { useEffect, useMemo, useState, useLayoutEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { usePublicSectionTheme } from "@/lib/publicTheme";
import { Briefcase, MapPin, ArrowRight, Building2, Clock } from "lucide-react";

/**
 * Public embeddable jobs board. Designed to be loaded inside an iframe.
 * Sends postMessage('portalwiz:resize') to the parent so the host page can auto-grow the iframe.
 */
export default function EmbedBoard() {
    const { slug } = useParams();
    const [params] = useSearchParams();
    const theme = (params.get("theme") || "light").toLowerCase();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeDept, setActiveDept] = useState(""); // "" = all
    const [sort, setSort] = useState("recent"); // "recent" | "urgency" | "title"
    const rootRef = useRef(null);

    // Pull the employer's "embed" section theme override (if any). Applied
    // via CSS custom properties on <html> as soon as we know the company id.
    usePublicSectionTheme(data?.company?.id, "embed");

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/public/embed/companies/${slug}/jobs`, {
                    withCredentials: false,
                    params: { sort },
                });
                setData(data);
            } catch (err) {
                setError(formatApiError(err));
            } finally { setLoading(false); }
        })();
    }, [slug, sort]);

    // Auto-resize host iframe — both grow and shrink to fit content. Includes
    // MutationObserver + fonts/images load + backstop polling so the host frame
    // always settles at the correct height even with async DOM growth.
    useLayoutEffect(() => {
        // Lock iframe document scroll — prevents content "middle slice"
        // cropping when an input/dropdown gets focused inside the iframe.
        const lock = () => { if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0); };
        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = "visible";
        document.body.style.overflow = "visible";
        window.addEventListener("scroll", lock, { passive: true });

        let lastSent = 0;
        const post = () => {
            if (!rootRef.current) return;
            const h = Math.ceil(rootRef.current.getBoundingClientRect().height) + 16;
            if (h <= 0 || Math.abs(h - lastSent) < 2) return;
            lastSent = h;
            try { window.parent?.postMessage({ kind: "portalwiz:resize", type: "portalwiz-resize", target: slug, height: h }, "*"); } catch { /* ignore */ }
        };
        post();
        const ro = new ResizeObserver(post);
        if (rootRef.current) ro.observe(rootRef.current);
        const mo = new MutationObserver(post);
        if (rootRef.current) mo.observe(rootRef.current, { subtree: true, childList: true, attributes: true, characterData: true });
        window.addEventListener("load", post);
        try { document.fonts?.ready?.then(post); } catch { /* ignore */ }
        const imgs = rootRef.current ? rootRef.current.querySelectorAll("img") : [];
        imgs.forEach((img) => img.addEventListener("load", post));
        const polls = [setTimeout(post, 200), setTimeout(post, 600), setTimeout(post, 1500), setTimeout(post, 3500)];
        return () => {
            ro.disconnect();
            mo.disconnect();
            window.removeEventListener("load", post);
            window.removeEventListener("scroll", lock);
            imgs.forEach((img) => img.removeEventListener("load", post));
            polls.forEach(clearTimeout);
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
        };
    }, [data, slug, activeDept, loading]);

    const isDark = theme === "dark";
    const palette = isDark
        ? { bg: "#0b1220", card: "#111a2e", border: "#1f2a44", text: "#e6ecff", sub: "#9aa6c0", accent: "#3b6dff", chip: "#1c2742", chipFg: "#cfd6ea", urg: "#fb923c" }
        : { bg: "#ffffff", card: "#ffffff", border: "#e2e8f0", text: "#0f172a", sub: "#64748b", accent: "#002FA7", chip: "#f1f5f9", chipFg: "#475569", urg: "#c2410c" };

    // Department breakdown
    const departments = useMemo(() => {
        if (!data?.jobs) return [];
        const map = new Map();
        for (const j of data.jobs) {
            const d = (j.department || "").trim();
            if (!d) continue;
            map.set(d, (map.get(d) || 0) + 1);
        }
        return [...map.entries()].sort((a, b) => b[1] - a[1]); // [name, count]
    }, [data]);

    const visibleJobs = useMemo(() => {
        if (!data?.jobs) return [];
        if (!activeDept) return data.jobs;
        return data.jobs.filter((j) => (j.department || "").trim() === activeDept);
    }, [data, activeDept]);

    const trackBrandingClick = () => {
        try {
            const u = new URL(`${API}/public/embed/branding-click`);
            u.searchParams.set("slug", slug);
            navigator.sendBeacon ? navigator.sendBeacon(u.toString()) : fetch(u.toString(), { method: "POST", keepalive: true });
        } catch { /* ignore */ }
    };

    return (
        <div
            ref={rootRef}
            style={{
                background: palette.bg, color: palette.text, padding: "20px 20px 24px",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
            data-testid="embed-board-root"
        >
            <style>{`
                .pw-card { transition: transform .15s ease, border-color .15s ease; }
                .pw-card:hover { transform: translateY(-1px); border-color: ${palette.accent}; }
                .pw-skill { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 10px; text-transform: uppercase; letter-spacing: .12em; }
                .pw-cta { color: ${palette.accent}; }
                a, a:visited { color: ${palette.text}; text-decoration: none; }
                @keyframes pw-pulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
                .pw-skel { animation: pw-pulse 1.4s ease-in-out infinite; background: ${palette.border}; height: 14px; border-radius: 2px; }
                .pw-dept { padding: 5px 11px; font-size: 11px; font-family: ui-monospace, monospace; text-transform: uppercase; letter-spacing: .12em; border: 1px solid ${palette.border}; cursor: pointer; transition: all .12s ease; background: ${palette.card}; color: ${palette.sub}; }
                .pw-dept:hover { border-color: ${palette.accent}; color: ${palette.text}; }
                .pw-dept.active { background: ${palette.accent}; color: #fff; border-color: ${palette.accent}; }
            `}</style>

            {loading ? (
                <EmbedSkeleton palette={palette} />
            ) : error ? (
                <div style={{ color: "#dc2626", fontSize: 14 }} data-testid="embed-error">{error}</div>
            ) : !data ? null : (
                <>
                    <div style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".18em", color: palette.accent }}>
                            Open roles at
                        </div>
                        <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.02em", margin: "4px 0 0 0" }}>{data.company?.name}</h1>
                        <div style={{ marginTop: 4, fontSize: 12, color: palette.sub }}>
                            {(data.jobs || []).length} active {((data.jobs || []).length === 1 ? "role" : "roles")}
                        </div>
                    </div>

                    {departments.length > 1 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }} data-testid="embed-dept-tabs">
                            <button
                                type="button"
                                onClick={() => setActiveDept("")}
                                className={`pw-dept ${!activeDept ? "active" : ""}`}
                                data-testid="embed-dept-all"
                            >
                                All · {(data.jobs || []).length}
                            </button>
                            {departments.map(([name, n]) => (
                                <button
                                    type="button"
                                    key={name}
                                    onClick={() => setActiveDept(name)}
                                    className={`pw-dept ${activeDept === name ? "active" : ""}`}
                                    data-testid={`embed-dept-${name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {name} · {n}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* B11 — Sort control. Server-side sorting keeps highlighted jobs floating on top. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 12, color: palette.sub }} data-testid="embed-sort-bar">
                        <span style={{ fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".14em", fontSize: 10 }}>Sort</span>
                        {[
                            { id: "recent",  label: "Newest" },
                            { id: "urgency", label: "Urgency" },
                            { id: "title",   label: "A → Z" },
                        ].map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setSort(opt.id)}
                                data-testid={`embed-sort-${opt.id}`}
                                style={{
                                    fontSize: 11,
                                    fontFamily: "ui-monospace, monospace",
                                    textTransform: "uppercase",
                                    letterSpacing: ".08em",
                                    padding: "4px 8px",
                                    border: `1px solid ${sort === opt.id ? palette.accent : palette.border}`,
                                    background: sort === opt.id ? palette.accent : "transparent",
                                    color: sort === opt.id ? "#fff" : palette.sub,
                                    cursor: "pointer",
                                }}
                            >{opt.label}</button>
                        ))}
                    </div>

                    {visibleJobs.length === 0 ? (
                        <div style={{ border: `1px dashed ${palette.border}`, padding: 32, textAlign: "center", color: palette.sub }} data-testid="embed-empty">
                            {activeDept ? `No active roles in ${activeDept} right now.` : "No active openings right now — check back soon."}
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: 12 }}>
                            {visibleJobs.map((j) => {
                                const href = `${window.location.origin}/j/${j.slug}?embed=${encodeURIComponent(slug)}&theme=${encodeURIComponent(theme)}`;
                                const urgent = j.urgency && j.urgency !== "Flexible";
                                const highlightColor = j.highlight ? (j.highlight_color || "#FBBF24") : null;
                                const onJobClick = () => {
                                    try { window.parent?.postMessage({ kind: "portalwiz:scroll-to-top", target: slug }, "*"); } catch { /* ignore */ }
                                };
                                return (
                                    <a
                                        key={j.id}
                                        href={href}
                                        rel="noopener"
                                        onClick={onJobClick}
                                        data-testid={`embed-job-${j.id}`}
                                        data-highlighted={highlightColor ? "true" : undefined}
                                        className="pw-card"
                                        style={{
                                            display: "block",
                                            padding: 18,
                                            background: palette.card,
                                            border: highlightColor ? `2px solid ${highlightColor}` : `1px solid ${palette.border}`,
                                            boxShadow: highlightColor ? `0 0 0 4px ${highlightColor}20` : undefined,
                                            position: "relative",
                                        }}
                                    >
                                        {highlightColor && (
                                            <span
                                                data-testid={`embed-job-highlight-${j.id}`}
                                                style={{
                                                    position: "absolute", top: -10, left: 12,
                                                    fontSize: 9, fontFamily: "ui-monospace, monospace",
                                                    letterSpacing: ".14em", textTransform: "uppercase",
                                                    padding: "2px 8px", background: highlightColor,
                                                    color: "#111", fontWeight: 800, border: `1px solid ${highlightColor}`,
                                                }}
                                            >★ Featured</span>
                                        )}
                                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                    <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>{j.title}</div>
                                                    {urgent && (
                                                        <span
                                                            style={{
                                                                fontFamily: "ui-monospace, monospace", fontSize: 9, fontWeight: 700,
                                                                textTransform: "uppercase", letterSpacing: ".15em",
                                                                padding: "2px 6px", border: `1px solid ${palette.urg}`, color: palette.urg,
                                                            }}
                                                            data-testid={`embed-urg-${j.id}`}
                                                        >
                                                            <Clock size={9} style={{ display: "inline", marginRight: 3, verticalAlign: -1 }} />
                                                            {j.urgency}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 12, color: palette.sub, marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", fontFamily: "ui-monospace, monospace" }}>
                                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MapPin size={12} /> {j.location || "—"}</span>
                                                    <span>·</span>
                                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Briefcase size={12} /> {j.work_mode || "On-site"}</span>
                                                    <span>·</span>
                                                    <span>{j.employment_type || "Full-time"} · {j.experience_min}–{j.experience_max} yrs</span>
                                                    {j.department && (
                                                        <>
                                                            <span>·</span>
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Building2 size={12} /> {j.department}</span>
                                                        </>
                                                    )}
                                                </div>
                                                {(j.required_skills || []).length > 0 && (
                                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                                                        {(j.required_skills || []).slice(0, 6).map((s) => (
                                                            <span key={s} className="pw-skill" style={{ padding: "3px 8px", border: `1px solid ${palette.border}`, color: palette.sub }}>{s}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="pw-skill pw-cta" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                Apply <ArrowRight size={12} />
                                            </span>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ marginTop: 20, textAlign: "right", fontSize: 11, color: palette.sub, fontFamily: "ui-monospace, monospace" }}>
                        Powered by{" "}
                        <a
                            href="https://portalwiz.com/talent-solutions-for-employers/"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={trackBrandingClick}
                            data-testid="embed-powered-by"
                            style={{ color: palette.accent, textDecoration: "underline", fontWeight: 700 }}
                        >
                            Portalwiz Talent Solutions
                        </a>
                    </div>
                </>
            )}
        </div>
    );
}

function EmbedSkeleton({ palette }) {
    return (
        <div data-testid="embed-loading">
            <div style={{ width: 120 }} className="pw-skel" />
            <div style={{ width: 260, height: 28, marginTop: 8, background: palette.border, borderRadius: 2 }} className="pw-skel" />
            <div style={{ display: "grid", gap: 12, marginTop: 22 }}>
                {[0, 1, 2].map((i) => (
                    <div key={i} style={{ padding: 18, border: `1px solid ${palette.border}`, background: palette.card }}>
                        <div className="pw-skel" style={{ width: "55%", height: 18 }} />
                        <div className="pw-skel" style={{ width: "85%", height: 11, marginTop: 10 }} />
                        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                            <div className="pw-skel" style={{ width: 60, height: 18 }} />
                            <div className="pw-skel" style={{ width: 50, height: 18 }} />
                            <div className="pw-skel" style={{ width: 70, height: 18 }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
